import { Api } from '@monorepo/shared/apiClient';
import {
  ViewCacheEvents,
  viewCacheRegister,
} from '@monorepo/shared/cacheRegisters/data2/viewCacheRegister';
import {
  getKeyParamsForInvalidation,
  getQueryConfig,
} from '@monorepo/shared/utils/queryUtils';
import { queryCache, QueryConfig, useQuery } from 'react-query';

type KeyParams = Api.FetchViewsParams;
const KEY_START = 'views';

const createQueryKey = ({ organizationId, projectId }: KeyParams) =>
  [KEY_START, organizationId, projectId] as const;

type Fetcher = Api.DataHookQueryFn<
  typeof createQueryKey,
  typeof Api.fetchViews
>;

type UseViewsParams = Partial<Api.FetchViewsParams> & {
  config?: QueryConfig<Api.FetchViewsReponse, Api.ErrorResponse>;
};

const fetcher: Fetcher = (
  _: string,
  organizationId: string,
  projectId: string,
) => Api.fetchViews({ organizationId, projectId });

export const useViews = ({
  config: inputConfig,
  organizationId,
  projectId,
}: UseViewsParams) => {
  const isEnabled = !!organizationId && !!projectId;
  const config = getQueryConfig<Api.FetchViewsReponse>(inputConfig, isEnabled);
  const key = isEnabled
    ? createQueryKey({ organizationId, projectId })
    : undefined;

  const { data: views, ...queryInfo } = useQuery(key, fetcher, config);

  const sortedViews = views?.sort((a, b) => a.name.localeCompare(b.name));

  return { views: sortedViews, ...queryInfo };
};

type InvalidateCacheParams = Omit<KeyParams, 'projectId'> & {
  organizationId: string;
  projectId?: string;
};

export async function invalidateCache(
  keyParams: InvalidateCacheParams,
): Promise<void> {
  await queryCache.invalidateQueries(
    getKeyParamsForInvalidation([
      KEY_START,
      keyParams.organizationId,
      keyParams.projectId,
    ]),
  );
}

type QueryCache = ReturnType<typeof useViews>['views'];
type NonEmptyQueryCache = Exclude<QueryCache, undefined>;

export function getCache(keyParams: KeyParams): QueryCache {
  return queryCache.getQueryData<QueryCache>(createQueryKey(keyParams));
}

export function setCache(
  keyParams: KeyParams,
  newItems: NonEmptyQueryCache,
): void {
  queryCache.setQueryData(createQueryKey(keyParams), newItems);
}

const cacheRegisterInvalidator = {
  hookName: 'useViews',
  callback: (keyParams: InvalidateCacheParams) => invalidateCache(keyParams),
};

viewCacheRegister(
  [ViewCacheEvents.CREATE, ViewCacheEvents.UPDATE, ViewCacheEvents.DELETE],
  cacheRegisterInvalidator,
);
